import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import {
  getFromLocalStorage,
  putToLocalStorage,
} from "@/services/localStorageService";
import { useSearchParams } from "react-router-dom";
import { getReplayURL } from "@/services/backendService";
import { useGameDataContext } from "./GameDataContext";
import {
  decodeBase64ToNumberArray,
  encodeNumberArrayToBase64,
} from "@/utils/number-array-encoder";

const VERSION = 1;

interface ActionBookmarkValues {
  addActionBookmark: (orderRank: number) => void;
  removeActionBookmark: (orderRank: number) => void;
  getReplayGameUrl: () => Promise<string | undefined>;
  actionBookmarks: number[];
}

export interface ActionBookmarksData {
  [key: string]: number[];
}

const notInitializedYet = (): any => {
  console.error("GameDataContext has not been initialized yet!");
};

const GameDataContext = createContext<ActionBookmarkValues>({
  addActionBookmark: notInitializedYet,
  removeActionBookmark: notInitializedYet,
  getReplayGameUrl: notInitializedYet,
  actionBookmarks: [],
});

const reducer: React.Reducer<
  ActionBookmarksData,
  | { type: "addActionBookmark"; samsId: string; orderRank: number }
  | { type: "addActionBookmarks"; samsId: string; orderRank: number[] }
  | { type: "removeActionBookmark"; samsId: string; orderRank: number }
> = (state, action) => {
  let game: number[] | undefined;
  switch (action.type) {
    case "addActionBookmarks":
    case "addActionBookmark":
      const newActionBookmarks = Array.isArray(action.orderRank)
        ? action.orderRank
        : [action.orderRank];
      game = state[action.samsId] || [];
      return {
        ...state,
        [action.samsId]: [...game, ...newActionBookmarks]
          .filter((value, index, array) => array.indexOf(value) === index)
          .sort(),
      };
    case "removeActionBookmark":
      game = state[action.samsId] || [];
      return {
        ...state,
        [action.samsId]: game.filter((fav) => fav !== action.orderRank).sort(),
      };
    default:
      return state;
  }
};

export const useActionBookmarksContext = (): ActionBookmarkValues =>
  useContext(GameDataContext);

const ActionBookmarksContextProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const gameDataContext = useGameDataContext();
  const game = gameDataContext.game;
  const [searchParams, setSearchParams] = useSearchParams();

  const localStorage = getFromLocalStorage("actionBookmarks");
  const games =
    ((localStorage?.version || 0) >= VERSION &&
      localStorage?.actionBookmarksByGame) ||
    {};
  const [actionBookmarksByGame, dispatch] = useReducer(reducer, games);
  const [actionBookmarks, setActionBookmarks] = useState<number[]>([]);

  useEffect(() => {
    console.log("Storing action bookmarks to local storage.");
    putToLocalStorage("actionBookmarks", {
      actionBookmarksByGame,
      version: VERSION,
    });
  }, [actionBookmarksByGame]);

  useEffect(() => {
    if (game && searchParams.has("bookmarks")) {
      const decodedActionBookmarks = decodeBase64ToNumberArray(
        searchParams.get("bookmarks"),
      );
      searchParams.delete("bookmarks");
      setSearchParams(searchParams);

      if (decodedActionBookmarks.length) {
        dispatch({
          type: "addActionBookmarks",
          samsId: game.samsId,
          orderRank: decodedActionBookmarks,
        });
      }
    }
  }, [searchParams, game]);

  useEffect(() => {
    if (game) {
      setActionBookmarks(actionBookmarksByGame[game.samsId] || []);
    } else {
      setActionBookmarks([]);
    }
  }, [game, actionBookmarksByGame]);

  const addActionBookmark: ActionBookmarkValues["addActionBookmark"] = (
    orderRank,
  ) => {
    const samsId = game?.samsId;
    if (!samsId) {
      console.error(
        "No samsId specified and no game loaded! Cannot add actionBookmark",
      );
    } else {
      dispatch({ type: "addActionBookmark", samsId, orderRank });
    }
  };

  const removeActionBookmark: ActionBookmarkValues["removeActionBookmark"] = (
    orderRank,
  ) => {
    const samsId = game?.samsId;
    if (!samsId) {
      console.error(
        "No samsId specified and no game loaded! Cannot remove actionBookmark",
      );
    } else {
      dispatch({ type: "removeActionBookmark", samsId, orderRank });
    }
  };

  const getReplayGameUrl: ActionBookmarkValues["getReplayGameUrl"] =
    async () => {
      const samsId = game?.samsId;
      if (!samsId) {
        console.error(
          "No samsId specified and no game loaded! Cannot get replay URL",
        );
        return undefined;
      } else {
        const actionBookmarks = actionBookmarksByGame[samsId] || [];
        return getReplayURL(samsId, encodeNumberArrayToBase64(actionBookmarks));
      }
    };

  return (
    <GameDataContext.Provider
      value={{
        addActionBookmark,
        removeActionBookmark,
        getReplayGameUrl,
        actionBookmarks,
      }}
    >
      {children}
    </GameDataContext.Provider>
  );
};

export default ActionBookmarksContextProvider;
